import { FlashbarProps } from '@amzn/awsui-components-core';
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface FlashMessage {
  readonly id: string;
  readonly type: FlashbarProps.Type;
  readonly content: ReactNode;
  readonly header?: ReactNode;
  /** Shows a spinner instead of the status icon. */
  readonly loading?: boolean;
  /** Shows the close button. */
  readonly dismissible?: boolean;
  /** Label of an optional action button, rendered by the banner. */
  readonly actionText?: string;
  readonly onAction?: () => void;
}

/** A new message. Passing an `id` replaces the message that has it, so a later state can update it. */
type NewFlashMessage = Omit<FlashMessage, 'id'> & { readonly id?: string };

interface FlashMessageContextType {
  readonly messages: readonly FlashMessage[];
  /** Adds (or, for a known `id`, replaces) a message and returns its id. */
  addMessage: (message: NewFlashMessage) => string;
  removeMessage: (id: string) => void;
}

interface FlashMessageProviderProps {
  readonly children: ReactNode;
}

const FlashMessageContext = createContext<FlashMessageContextType | undefined>(undefined);

export const FlashMessageProvider = ({ children }: FlashMessageProviderProps): JSX.Element => {
  const [messages, setMessages] = useState<FlashMessage[]>([]);

  const addMessage = useCallback((message: NewFlashMessage): string => {
    const id = message.id ?? uuidv4();
    const next: FlashMessage = { ...message, id };
    setMessages((prev) =>
      prev.some((existing) => existing.id === id) ? prev.map((existing) => (existing.id === id ? next : existing)) : [...prev, next]
    );
    return id;
  }, []);

  const removeMessage = useCallback((id: string): void => {
    setMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  const contextValue: FlashMessageContextType = {
    messages,
    addMessage,
    removeMessage
  };

  return <FlashMessageContext.Provider value={contextValue}>{children}</FlashMessageContext.Provider>;
};

export const useFlashMessage = (): FlashMessageContextType => {
  const context = useContext(FlashMessageContext);
  if (context === undefined) {
    throw new Error('useFlashMessage must be used within a FlashMessageProvider');
  }
  return context;
};
